import { VuexModule, Module, Action, MutationAction, Mutation } from "vuex-module-decorators";
import Store from "../../index";
import { DeliveryForListResource } from "@/models/api/system/deliveryFor";
import { FacilityListResource } from "@/models/api/system/facilities";
import { FacilityDetailsResource, SaveFacilityDetails } from "@/models/api/system/facilityDetails";
import { rawApi } from "@/lib/api";
import { DeliveryTypeListResource } from "@/models/api/system/deliveryTypes";
import { ReferenceTypeListResource } from "@/models/api/system/referenceTypes";
import { Manufacturer, ManufacturerSearchCriteria } from "@/models/api/system/manufacturers";
import {
  Commodity,
  CommoditySearchCriteria,
  CommodityFixForOption
} from "@/models/api/system/commodities";
import { State } from "@/models/api/system/states";
import {
  RoleListResource,
  RoleResource,
  CreateRoleRequest,
  CreateRoleResponse,
  UpdateRoleRequest,
  UpdateRoleResponse
} from "@/models/api/system/roles";
import {
  GroupListResource,
  GroupResource,
  SaveGroupRequest,
  SaveGroupResponse
} from "@/models/api/system/groups";
import {AxiosPromise, AxiosResponse} from "axios";
import {
  GetUsersRequest,
  SearchUsersRequest,
  GetUsersResponse,
  UserListResource,
  UserResource,
  SaveUserRequest,
  SaveUserResponse
} from "@/models/api/system/users";
import {
  GetAgentUsersRequest,
  SearchAgentUsersRequest,
  GetAgentUsersResponse,
  AgentUserListResource,
  AgentUserResource,
  SaveAgentUserRequest,
  SaveAgentUserResponse
} from "@/models/api/system/agentUsers";
import { GetAgentsRequest,
  GetAgentsResponse,
  AgentListResource,
  AgentResource,
  AgentDeliveryForResource,
  AgentFacilityResource,
  AgentManufacturerResource,
  AgentCompanyResource,
  AgentCommodityResource,
  AgentReferenceTypeResource,
  AgentAccessorialChargeTypeResource,
  GetAgentUserDetailsRequest,
  AgentSettingResource } from "@/models/api/system/agents";
import { UnitOfMeasure } from "@/models/api/system/unitOfMeasure";
import { ChargeTypeResource,AgentChargeTypeResource } from "@/models/api/system/chageTypes";
import { ReasonCodeResource } from "@/models/api/system/reasonCodes";
import { OrderStatusResource } from "@/models/api/system/orderStatus";
import { TruckStatusResource } from "@/models/api/system/truckStatus";
import {
  GetBillToCommoditiesRequest,
  BillToCommodityCodeResource,
  CreateBillToCommodityRequest,
  CreateBillToCommodityResponse,
  UpdateBillToCommodityRequest,
  UpdateBillToCommodityResponse
} from "@/models/api/system/billToCommodities";
import {
  GetBillToManufacturerCodesRequest,
  BillToManufacturerCodeResource,
  CreateBillToManufacturerRequest,
  CreateBillToManufacturerResponse,
  UpdateBillToManufacturerRequest,
  UpdateBillToManufacturerResponse
} from "@/models/api/system/billToManufacturers";
import {
  CommodityUnloadTimeResource,
  GetCommodityUnloadTimesRequest,
  CreateCommodityUnloadTimeRequest,
  CreateCommodityUnloadTimeResource
} from "@/models/api/system/commodityUnloadTime";
import {
  CommodityUnloadTimesEffectiveResource,
  GetCommodityUnloadTimesEffectiveRequest,
  CreateCommodityUnloadTimeOverrideRequest
} from "@/models/api/system/commodityUnloadTimeOverride";
import {
  CustomerOnHoldResource,
  GetCustomerOnHoldRequest,
  GetCustomerOnHoldResponse
} from "@/models/api/system/customerOnHold";
import {
  GetTimeframeSchedulingSettingsRequest,
  TimeframeSchedulingSettings,
  SaveTimeframeSchedulingSettingsRequest,
  ResetTimeframeSchedulingSettingsRequest
} from "@/models/api/system/timeframeSchedulingSettings";
import _ from "lodash";
import { ISystemStore } from "@/store/types";
import { Zip3Override } from "@/models/api/system/zip3Override";
import { originAddress } from "@/models/api/system/timeframeOriginAddress";

const name = "system";

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true, stateFactory: true })
export default class SystemModule extends VuexModule implements ISystemStore {
  error: string = null;
  loading: boolean = false;
  saving: boolean = false;
  modalToDisplay: string = null;
  modalData: any = null;
  errorDetails: any = null;
  statusOptions: Array<any> = [{ name: "Active" }, { name: "Inactive" }];

  deliveryTypes: Array<any> = [];
  facilityTypes: Array<any> = [
    { name: "Office building" },
    { name: "Warehouse" },
    { name: "Tower Building" },
    { name: "Single family house" },
    { name: "Condo" },
    { name: "Apartment" }
  ];

  manufacturerTypes: Array<any> = [{ name: "Factory" }, { name: "Industry Building" }];
  deliveryFors: Array<DeliveryForListResource> = [];
  facilities: Array<FacilityListResource> = [];
  agentFacilities: Array<FacilityListResource> = [];
  cdsFacilities: Array<string> = null;
  referenceTypes: Array<any> = [];
  commodities: Array<any> = [];
  states: Array<any> = [];
  zip3Overrides: Array<any> = [];
  actionResults: Array<any> = null;
  chargeTypes: ChargeTypeResource[] = null;
  commodityFixForOptions: CommodityFixForOption[] = null;
  reasonCodes: ReasonCodeResource[] = null;
  orderStatuses: OrderStatusResource[] = null;
  truckStatuses: TruckStatusResource[] = null;
  agentDeliveryTypes: Array<any> = [];
  allAgentDeliveryTypes: Array<any> = [];


  @Action
  async loadDeliveryFors(): Promise<void> {
    const response = await rawApi.get<Array<DeliveryForListResource>>(`/deliveryFor`);
    this.setDeliveryFors(response.data);
  }

  @Action
  showModal(modal: any): void {
    console.log(`showModal: ${modal.name}`);
    this.setCurrentModal(modal.name);
    if (modal.data) this.setModalData(_.cloneDeep(modal.data));
    else this.setModalData(null);
  }

  @Action
  showErrorDetailModal(errorDetails: any): void {
    this.setCurrentModal("Error Details");
  }

  @Action
  addModalOrder(order: any): void {
    this.addModalSeletedItem(_.cloneDeep(order));
  }

  @Action
  async loadFacilities(): Promise<void> {
    const response = await rawApi.get<Array<FacilityListResource>>(`/facilities`);
    this.setFacilities(response.data);
}

  @Action
  async loadAgentFacilities(deliveryForId?: number): Promise<void> {
    const response = !deliveryForId ?
        await rawApi.get<Array<FacilityListResource>>(`/facilities`) :
        await rawApi.get<Array<FacilityListResource>>(`/facilities?deliveryForId=${deliveryForId}`);

    this.setAgentFacilities(response.data);
}
  @Action
  async loadAgentFacilitiesByAgentId(agentId?: number): Promise<void> {
    if(!agentId) {
      const response = await rawApi.get<Array<FacilityListResource>>(`/facilities`);
      this.setAgentFacilities(response.data);
    } else {
      const response = await rawApi.get<Array<FacilityListResource>>(`/facilities?agentId=${agentId}`);
      this.setAgentFacilities(response.data);
    }
  }

  @Action
  async loadFacilitiesWithInclude(): Promise<void> {
    const response = await rawApi.get<Array<FacilityListResource>>(`/facilities/includeInactive`);
    this.setFacilities(response.data);
  }

  @Action
  async loadAgentDeliveryTypesForStartup(): Promise<void> {
    const result = await rawApi.post('/deliveryTypes/agent-delivery-types/startup', this.deliveryFors.map(e => e.id));
    this.setAllAgentDeliveryTypes(result.data);
  }

  @Action
  async loadDeliveryTypes(): Promise<void> {
    let promise: Promise<AxiosResponse<DeliveryTypeListResource[]>> = null;

    if(this.deliveryFors.length > 0) {
      const deliveryFors = _.map(this.deliveryFors, (deliveryType: DeliveryForListResource) => {
        return deliveryType.id;
      })
      const uniqueAgents = _.uniq(deliveryFors);
      const joinedAgents = uniqueAgents.join(",");
      promise = rawApi.get<DeliveryTypeListResource[]>('/deliveryTypes?deliveryForIds=' + joinedAgents);
    } else {
      promise = rawApi.get<DeliveryTypeListResource[]>(`/deliveryTypes`);
    }

    if(!promise) {
      return;
    }

    const response = await promise;
    this.setDeliveryTypes(response.data);
  }

  @Action
  async loadChargeTypes(deliveryForId: number): Promise<void> {
    if(!deliveryForId) {
      const response = await rawApi.get<ChargeTypeResource[]>(`/chargeTypes`);
      this.setChargeTypes(response.data);
    } else {
      const agentResponse = await rawApi.get<AgentChargeTypeResource[]>(`/agentAccessorialChargeType?deliveryForId=${deliveryForId}`);
      let response:ChargeTypeResource[]  = agentResponse.data.map(e => ({"itemCode": e.code, "description": e.description}));
      this.setChargeTypes(response);
    }
  }

  @Action
  async loadReferenceTypes(deliveryForId: number|null = null): Promise<void> {
    const response = await rawApi.get<ReferenceTypeListResource[]>(`/referenceTypes?deliveryForId=${deliveryForId??0}`);

    this.setReferenceTypes(response.data);
  }

  @Action
  async loadCommodityFixForOptions(): Promise<void> {
    const response = await rawApi.get<CommodityFixForOption[]>(`/commodityFixForOptions`);

    this.setCommodityFixForOptions(response.data);
  }

  @Action
  async getReasonCodesByCompany(companyId: string): Promise<ReasonCodeResource[]> {
    const response = await rawApi.get<ReasonCodeResource[]>(
      `/ediReasonCodes?companyId=${companyId}`
    );

    return response.data;
  }

  @Action
  async loadOrderStatuses(): Promise<void> {
    const response = await rawApi.get<OrderStatusResource[]>(`/orderStatuses`);

    this.setOrderStatuses(response.data);
  }
  @Action
  async loadTruckStatuses(): Promise<void> {
    const response = await rawApi.get<TruckStatusResource[]>(`/truckStatuses`);

    this.setTruckStatuses(response.data);
  }

  @Action
  async getUnitsOfMeasure(type: string, name: string = null): Promise<UnitOfMeasure[]> {
    let validUnitsOfMeasure = ["count", "weight", "volume", "charge"];

    if (validUnitsOfMeasure.indexOf(type.toLowerCase()) < 0)
      throw new Error("Not a valid unit of measure");

    let url = `/unitOfMeasures/${type}`;

    if (name) url += `?name=${name}`;

    const response = await rawApi.get<UnitOfMeasure[]>(url);

    return response.data;
  }

  @Action
  async searchManufacturersByName(criteria: ManufacturerSearchCriteria): Promise<Manufacturer[]> {
    const deliveryForId = criteria.deliveryForId !== undefined ?
      criteria.deliveryForId : null;

    let url = `/manufacturers?name=`;
    if (criteria.name) url += `${criteria.name}`;
    if (criteria.billTo) url += `&billTo=${criteria.billTo}`;
    if (criteria.deliveryForId !== undefined && criteria.deliveryForId !== null) url += `&deliveryForId=${criteria.deliveryForId}`;
    const response = await rawApi.get<Manufacturer[]>(url);
    return response.data;
  }

  @Action
  async getManufacturer(id: string) : Promise<Manufacturer> {
    let url = `/manufacturers/${id}`;
    const response = await rawApi.get<Manufacturer>(url);
    return response.data;
  }

  @Action
  async searchCommoditiesByCode(criteria: CommoditySearchCriteria): Promise<Commodity[]> {
    const deliveryForId = criteria.deliveryForId !== undefined ?
      criteria.deliveryForId : null;
    let url = `/commodities?`;
    if (criteria.code) url += `code=${criteria.code}`;
    if (criteria.billTo) url += `&billTo=${criteria.billTo}`;
    if (criteria.deliveryForId !== undefined && criteria.deliveryForId !== null) url += `&deliveryForId=${criteria.deliveryForId}`;
    const response = await rawApi.get<Commodity[]>(url);
    return response.data;
  }

  @Action
  async loadStates(): Promise<void> {
    const response = await rawApi.get<State[]>("/system/states");

    this.setStates(response.data);
  }

  @Action
  async loadZip3Overrides(): Promise<void> {
    const response = await rawApi.get<Zip3Override[]>("/system/zip3Overrides");

    this.setZip3Overrides(response.data);
  }

  @Action
  async getRoles(agentId: number = 0): Promise<RoleListResource[]> {
    const response = await rawApi.get<RoleListResource[]>(`/system/roles?agentId=${agentId??0}`);

    return response.data;
  }
  @Action
  async getAllRoles(): Promise<RoleListResource[]> {
    const response = await rawApi.get<RoleListResource[]>(`/system/roles`);

    return response.data;
  }


  @Action
  async getRoleDetail(id: number): Promise<RoleResource> {
    const response = await rawApi.get<RoleResource>(`/system/roles/${id}`);

    return response.data;
  }

  @Action
  async createRole(request: CreateRoleRequest): Promise<CreateRoleResponse> {
    const response = await rawApi.post<CreateRoleResponse>(`/system/roles`, request);

    return response.data;
  }

  @Action
  async updateRole(request: UpdateRoleRequest): Promise<UpdateRoleResponse> {
    const response = await rawApi.put<UpdateRoleResponse>(`/system/roles/${request.id}`, request);

    return response.data;
  }

  @Action
  async getGroups(): Promise<GroupListResource[]> {
    const response = await rawApi.get<GroupListResource[]>(`/system/groups`);

    return response.data;
  }

  @Action
  async getGroupDetail(id: number): Promise<GroupResource> {
    return (await rawApi.get<GroupResource>(`/system/groups/${id}`)).data;
  }

  @Action
  async saveGroup(request: SaveGroupRequest): Promise<SaveGroupResponse> {
    let response: AxiosResponse<SaveGroupResponse> = null;

    if (!request.isNew)
      response = await rawApi.put<SaveGroupResponse>(`/system/groups/${request.id}`, request);
    else response = await rawApi.post<SaveGroupResponse>(`/system/groups`, request);

    return response.data;
  }

  @Action
  async getAgents(request: GetAgentsRequest): Promise<GetAgentsResponse> {
    const response = await rawApi.get<AgentListResource[]>(
      `/agent?pageNumber=${request.pageNumber}&pageSize=${request.pageSize}`
    );

    let total: number = 0;

    if (response.headers.totalitems) {
      total = response.headers.totalitems;
    }

    return new GetUsersResponse(response.data, total);
  }

  @Action
  async getAgent(id): Promise<AgentResource> {
    const response = await rawApi.get<AgentResource>(`/agent/${id}`);
    return response.data;
  }

  @Action
  async postAgent(agent: AgentResource) : Promise<AgentResource> {
    const response = await rawApi.post<AgentResource>('/agent', agent);
    return response.data;
  }

  @Action
  async putAgent(agent: AgentResource) : Promise<AgentResource> {
    const response = await rawApi.put<AgentResource>(`/agent/${agent.id}`, agent);
    return response.data;
  }

  @Action
  async getAgentDeliveryFors(agentId: number) : Promise<AgentDeliveryForResource[]> {
    const response = await rawApi.get<AgentDeliveryForResource[]>(`/deliveryFor?agentId=${agentId}`);
    return response.data;
  }

  @Action
  async postAgentDeliveryFor(deliveryFor: AgentDeliveryForResource) : Promise<AgentDeliveryForResource> {
    const response = await rawApi.post<AgentDeliveryForResource>('/deliveryFor', deliveryFor);
    return response.data;
  }

  @Action
  async putAgentDeliveryFor(deliveryFor: AgentDeliveryForResource) : Promise<AgentDeliveryForResource> {
    const response = await rawApi.put<AgentDeliveryForResource>(`/deliveryFor/${deliveryFor.id}`, deliveryFor);
    return response.data;
  }

  @Action
  async deleteAgentDeliveryFor(id: number) : Promise<void> {
    let url = `/deliveryFor/${id}`;
    await rawApi.delete(url);
  }

  @Action
  async getAgentFacilities(agentId: number) : Promise<AgentFacilityResource[]> {
    const response = await rawApi.get<AgentFacilityResource[]>(`/agentFacility?agentId=${agentId}`);
    return response.data;
  }

  @Action
  async postAgentFacility(agentFacility: AgentFacilityResource) : Promise<AgentFacilityResource> {
    const response = await rawApi.post<AgentFacilityResource>('/agentFacility', agentFacility);
    return response.data;
  }

  @Action
  async deleteAgentFacility(facilityCode: string) : Promise<void> {
    let url = `/agentFacility/${facilityCode}`;
    await rawApi.delete(url);
  }

  @Action
  async getAgentManufacturers(agentId: number) : Promise<AgentManufacturerResource[]> {
    const response = await rawApi.get<AgentManufacturerResource[]>(`/agentManufacturer?agentId=${agentId}`);
    return response.data;
  }

  @Action
  async postAgentManufacturer(agentManufacturer: AgentManufacturerResource) : Promise<AgentManufacturerResource> {
    const response = await rawApi.post<AgentManufacturerResource>('/agentManufacturer', agentManufacturer);
    return response.data;
  }

  @Action
  async putAgentManufacturer(agentManufacturer: AgentManufacturerResource) : Promise<AgentManufacturerResource> {
    const response = await rawApi.put<AgentManufacturerResource>(`/agentManufacturer/${agentManufacturer.manufacturerCode}`, agentManufacturer);
    return response.data;
  }

  @Action
  async deleteAgentManufacturer(manufacturerCode: string) : Promise<void> {
    let url = `/agentManufacturer/${manufacturerCode}`;
    await rawApi.delete(url);
  }

  @Action
  async getAgentCompanies(agentId: number) : Promise<AgentCompanyResource[]> {
    const response = await rawApi.get<AgentCompanyResource[]>(`/agentCompany?agentId=${agentId}`);
    return response.data;
  }

  @Action
  async postAgentCompany(agentCompany: AgentCompanyResource) : Promise<AgentCompanyResource> {
    const response = await rawApi.post<AgentCompanyResource>('/agentCompany', agentCompany);
    return response.data;
  }

  @Action
  async putAgentCompany(agentCompany: AgentCompanyResource) : Promise<AgentCompanyResource> {
    const response = await rawApi.put<AgentCompanyResource>(`/agentCompany/${agentCompany.companyId}`, agentCompany);
    return response.data;
  }

  @Action
  async getAgentCommodities(agentId: number) : Promise<AgentCommodityResource[]> {
    const response = await rawApi.get<AgentCommodityResource[]>(`/agentCommodity?agentId=${agentId}`);
    return response.data;
  }

  @Action
  async postAgentCommodity(agentCommodity: AgentCommodityResource) : Promise<AgentCommodityResource> {
    const response = await rawApi.post<AgentCommodityResource>('/agentCommodity', agentCommodity);
    return response.data;
  }

  @Action
  async putAgentCommodity(agentCommodity: AgentCommodityResource) : Promise<AgentCommodityResource> {
    const response = await rawApi.put<AgentCommodityResource>(`/agentCommodity/${agentCommodity.commodityCode}`, agentCommodity);
    return response.data;
  }

  @Action
  async getAgentReferenceTypes(agentId: number) : Promise<AgentReferenceTypeResource[]> {
    const response = await rawApi.get<AgentReferenceTypeResource[]>(`/agentReferenceType?agentId=${agentId}`);
    return response.data;
  }

  @Action
  async postAgentReferenceType(agentReferenceType: AgentReferenceTypeResource) : Promise<AgentReferenceTypeResource> {
    const response = await rawApi.post<AgentReferenceTypeResource>('/agentReferenceType', agentReferenceType);
    return response.data;
  }

  @Action
  async putAgentReferenceType(agentReferenceType: AgentReferenceTypeResource) : Promise<AgentReferenceTypeResource> {
    const response = await rawApi.put<AgentReferenceTypeResource>(`/agentReferenceType/${agentReferenceType.id}`, agentReferenceType);
    return response.data;
  }

  @Action
  async deleteAgentReferenceType(id: number) : Promise<void> {
    let url = `/agentReferenceType/${id}`;
    await rawApi.delete(url);
  }

  @Action
  async getAgentAccessorialChargeTypes(agentId: number) : Promise<AgentAccessorialChargeTypeResource[]> {
    const response = await rawApi.get<AgentAccessorialChargeTypeResource[]>(`/agentAccessorialChargeType?agentId=${agentId}`);
    return response.data;
  }

  @Action
  async postAgentAccessorialChargeType(agentAccessorialChargeType: AgentAccessorialChargeTypeResource) : Promise<AgentAccessorialChargeTypeResource> {
    const response = await rawApi.post<AgentAccessorialChargeTypeResource>('/agentAccessorialChargeType', agentAccessorialChargeType);
    return response.data;
  }

  @Action
  async putAgentAccessorialChargeType(agentAccessorialChargeType: AgentAccessorialChargeTypeResource) : Promise<AgentAccessorialChargeTypeResource> {
    const response = await rawApi.put<AgentAccessorialChargeTypeResource>(`/agentAccessorialChargeType/${agentAccessorialChargeType.id}`, agentAccessorialChargeType);
    return response.data;
  }

  @Action
  async deleteAgentAccessorialChargeTypee(id: number) : Promise<void> {
    let url = `/agentAccessorialChargeType/${id}`;
    await rawApi.delete(url);
  }

  @Action
  async getAgentSettings(agentId: number) : Promise<AgentSettingResource[]> {
    const response = await rawApi.get<AgentSettingResource[]>(`/agentSetting?agentId=${agentId}`);
    return response.data;
  }

  @Action
  async postAgentSetting(agentSetting: AgentSettingResource) : Promise<AgentSettingResource> {
    const response = await rawApi.post<AgentSettingResource>('/agentSetting', agentSetting);
    return response.data;
  }

  @Action
  async putAgentSetting(agentSetting: AgentSettingResource) : Promise<AgentSettingResource> {
    const response = await rawApi.put<AgentSettingResource>(`/agentSetting/${agentSetting.id}`, agentSetting);
    return response.data;
  }

  @Action
  async deleteAgentSetting(id: number) : Promise<void> {
    let url = `/agentSetting/${id}`;
    await rawApi.delete(url);
  }

  @Action
  async getUsers(request: GetUsersRequest): Promise<GetUsersResponse> {
    const response = await rawApi.get<UserListResource[]>(
      `/system/users?pageNumber=${request.pageNumber}&pageSize=${request.pageSize}`
    );
    let total: number = 0;

    if (response.headers.totalitems) {
      total = response.headers.totalitems;
    }

    return new GetUsersResponse(response.data, total);
  }

  @Action
  async getAgentUsers(request: GetAgentUsersRequest): Promise<GetAgentUsersResponse> {
    const filter = request.agentId == null ? "" : `agentId=${request.agentId}`
    const response = await rawApi.get<AgentUserListResource[]>(
      `/system/agentusers?${filter}`
    );
    let total: number = 0;

    if (response.headers.totalitems) {
      total = response.headers.totalitems;
    }
    return new GetAgentUsersResponse(response.data, total);
  }

  @Action
  async searchUsers(request: SearchUsersRequest): Promise<GetUsersResponse> {
    const response = await rawApi.get<UserListResource[]>(
      `/system/users/SearchByName?name=${request.name}&pageNumber=${request.pageNumber}&pageSize=${request.pageSize}`
    );
    let total: number = 0;

    if (response.headers.totalitems) {
      total = response.headers.totalitems;
    }

    return new GetUsersResponse(response.data, total);
  }

  @Action
  async searchAgentUsers(request: SearchAgentUsersRequest): Promise<GetAgentUsersResponse> {
    const response = await rawApi.get<AgentUserListResource[]>(
      `/system/agentusers/SearchByName?name=${request.name}&pageNumber=${request.pageNumber}&pageSize=${request.pageSize}`
    );
    let total: number = 0;

    if (response.headers.totalitems) {
      total = response.headers.totalitems;
    }

    return new GetAgentUsersResponse(response.data, total);
  }

  @Action
  async getUserDetail(id): Promise<UserResource> {
    const response = await rawApi.get<UserResource>(`/system/users/${id}`);

    return response.data;
  }

  @Action
  async getAgentUserDetail(request: GetAgentUserDetailsRequest): Promise<AgentUserResource> {
    const filter = request.agentId == null ? "" : `agentId=${request.agentId}`;
    const response = await rawApi.get<AgentUserResource>(`/system/agentUsers/${request.externalId}?${filter}`);

    return response.data;
  }

  @Action
  async saveUser(request: SaveUserRequest): Promise<SaveUserResponse> {
    let response: AxiosResponse<SaveUserResponse>;
    if (!request.isNew)
      response = await rawApi.put<SaveUserResponse>(`/system/users/${request.id}`, request);
    else response = await rawApi.post<SaveUserResponse>(`/system/users`, request);

    return response.data;
  }

  @Action
  async saveAgentUser(request: SaveAgentUserRequest): Promise<SaveAgentUserResponse> {
    let response: AxiosResponse<SaveAgentUserResponse>;
    if (!request.isNew)
      response = await rawApi.put<SaveAgentUserResponse>(`/system/agentusers/${request.externalId}`, request);
    else
      response = await rawApi.post<SaveAgentUserResponse>(`/system/agentusers`, request);

    return response.data;
  }

  @Action
  updateIsSaving(isSaving: boolean) {
    this.setSaving(isSaving);
  }

  @Action
  async getBillToCommodityCodes(
    request: GetBillToCommoditiesRequest
  ): Promise<BillToCommodityCodeResource[]> {
    let url = "/billToCommodities?";

    if (request.billTo) url += `billTo=${encodeURIComponent(request.billTo)}&`;

    if (request.code) url += `code=${encodeURIComponent(request.code)}`;

    const response = await rawApi.get<BillToCommodityCodeResource[]>(url);

    return response.data;
  }

  @Action
  async getBillToCommodityCode(id: number): Promise<BillToCommodityCodeResource> {
    let url = `/billToCommodities/${id}`;

    const response = await rawApi.get<BillToCommodityCodeResource>(url);

    return response.data;
  }

  @Action
  async createBillToCommodityCode(
    request: CreateBillToCommodityRequest
  ): Promise<CreateBillToCommodityResponse> {
    let url = `/billToCommodities`;

    const response = await rawApi.post<CreateBillToCommodityResponse>(url, request);

    return response.data;
  }

  @Action
  async updateBillToCommodityCode(
    request: UpdateBillToCommodityRequest
  ): Promise<UpdateBillToCommodityResponse> {
    let url = `/billToCommodities/${request.id}`;

    const response = await rawApi.put<CreateBillToCommodityResponse>(url, request);

    return response.data;
  }

  @Action
  async deleteBillToCommodityCode(id: number): Promise<void> {
    let url = `/billToCommodities/${id}`;

    await rawApi.delete(url);
  }

  @Action
  async getBillToManufacturerCodes(
    request: GetBillToManufacturerCodesRequest
  ): Promise<BillToManufacturerCodeResource[]> {
    let url = "/billToManufacturers?";

    if (request.billTo) url += `billTo=${encodeURIComponent(request.billTo)}&`;

    if (request.code) url += `code=${encodeURIComponent(request.code)}`;

    const response = await rawApi.get<BillToManufacturerCodeResource[]>(url);

    return response.data;
  }

  @Action
  async getBillToManufacturerCode(id: number): Promise<BillToManufacturerCodeResource> {
    let url = `/billToManufacturers/${id}`;

    const response = await rawApi.get<BillToManufacturerCodeResource>(url);

    return response.data;
  }

  @Action
  async createBillToManufacturerCode(
    request: CreateBillToManufacturerRequest
  ): Promise<CreateBillToManufacturerResponse> {
    let url = `/billToManufacturers`;

    const response = await rawApi.post<CreateBillToManufacturerResponse>(url, request);

    return response.data;
  }

  @Action
  async updateBillToManufacturerCode(
    request: UpdateBillToManufacturerRequest
  ): Promise<UpdateBillToManufacturerResponse> {
    let url = `/billToManufacturers/${request.id}`;

    const response = await rawApi.put<UpdateBillToManufacturerResponse>(url, request);

    return response.data;
  }

  @Action
  async deleteBillToManufacturerCode(id: number): Promise<void> {
    let url = `/billToManufacturers/${id}`;

    await rawApi.delete(url);
  }

  @Action
  async getCustomerOnHold(
    request: GetCustomerOnHoldRequest
  ): Promise<GetCustomerOnHoldResponse> {
    let url = `/customerOnHold/${encodeURIComponent(request.billToCompanyId)}?`;

    if (request.pageNumber) url += `page=${request.pageNumber}&`;
    if (request.pageSize) url += `pageSize=${request.pageSize}`;

    const response = await rawApi.get<GetCustomerOnHoldResponse>(url);

    return response.data;
  }

  @Action
  async placeCustomerOnHold(billToCompanyId: string): Promise<void> {
    const url = `/customerOnHold/${encodeURIComponent(billToCompanyId)}`;
    await rawApi.put(url);
  }

  @Action
  async removeCustomerFromHold(billToCompanyId: string): Promise<void> {
    const url = `/customerOnHold/${encodeURIComponent(billToCompanyId)}`;
    await rawApi.delete(url);
  }

  @Action
  async getCommodityUnloadTimes(
    request: GetCommodityUnloadTimesRequest
  ): Promise<CommodityUnloadTimeResource[]> {
    let url = "/commodityUnloadTimes?";

    if (request.commodityCode) url += `commodityCode=${encodeURIComponent(request.commodityCode)}`;

    const response = await rawApi.get<CommodityUnloadTimeResource[]>(url);

    return response.data;
  }

  @Action
  async createCommodityUnloadTimes(
    request: CreateCommodityUnloadTimeRequest
  ): Promise<CreateCommodityUnloadTimeResource> {
    let url = `/commodityUnloadTimes`;

    const response = await rawApi.post<CreateCommodityUnloadTimeResource>(url, request);

    return response.data;
  }

  @Action
  async deleteCommodityUnloadTimes(commodityCode: string): Promise<void> {
    let url = `/commodityUnloadTimes/${commodityCode}`;

    await rawApi.delete(url);
  }

  @Action
  async getCommodityUnloadTimesEffective(
    request: GetCommodityUnloadTimesEffectiveRequest
  ): Promise<CommodityUnloadTimesEffectiveResource[]> {
    let url = "commodityUnloadTimeOverrides/effective?";

    if (request.commodityCode) url += `commodityCode=${encodeURIComponent(request.commodityCode)}&`;
    if (request.facilityCode) url += `facilityCode=${encodeURIComponent(request.facilityCode)}&`;
    if (request.overriddenOnly)
      url += `overriddenOnly=${request.overriddenOnly ? "true" : "false"}`;

    const response = await rawApi.get<CommodityUnloadTimesEffectiveResource[]>(url);

    return response.data;
  }

  @Action
  async createCommodityUnloadTimeOverride(request: CreateCommodityUnloadTimeRequest) {
    let url = `/commodityUnloadTimeOverrides`;
    await rawApi.post(url, request);
  }

  @Action
  async deleteCommodityUnloadTimeOverride(id: number): Promise<void> {
    let url = `/commodityUnloadTimeOverrides/${id}`;

    await rawApi.delete(url);
  }

  @Action
  async getTimeframeSchedulingSettings(
    request: GetTimeframeSchedulingSettingsRequest
  ): Promise<TimeframeSchedulingSettings> {
    let url = "timeframeSchedulingSettings";

    if (request.facilityCode) url += `/${encodeURIComponent(request.facilityCode)}`;

    const response = await rawApi.get<TimeframeSchedulingSettings>(url);

    return response.data;
  }

  @Action
  async saveTimeframeSchedulingSettings(request: SaveTimeframeSchedulingSettingsRequest) {
    let url = `/timeframeSchedulingSettings`;
    if (request.facilityCode) url += `/${encodeURIComponent(request.facilityCode)}`;
    await rawApi.put(url, request);
  }

  @Action
  async resetTimeframeSchedulingSettings(facilityCode: string): Promise<void> {
    let url = `/timeframeSchedulingSettings/${facilityCode}`;

    await rawApi.delete(url);
  }

  @Action
  async getTimeframeSchedulingDefaultSettings(): Promise<TimeframeSchedulingSettings> {
    let url = "timeframeSchedulingSettings/default";

    const response = await rawApi.get<TimeframeSchedulingSettings>(url);

    return response.data;
  }

  @Action
  async saveTimeframeSchedulingDefaultSettings(request: SaveTimeframeSchedulingSettingsRequest) {
    let url = `/timeframeSchedulingSettings/default`;
    await rawApi.put(url, request);
  }


  @Action
  async getTimeframeOriginAddress(facilityCode: string): Promise<originAddress> {
    const response = await rawApi.get(`/timeframeOriginAddresses/${facilityCode}`);

    return response.data;
  }

  @Action
  async getFacilities(wantBuilders: string = "Y"): Promise<GetUsersResponse> {
    const response = await rawApi.get<FacilityDetailsResource[]>(
      `/facilities/includeInactive?wantBuilders=${wantBuilders}`
    );
    let total: number = 0;

    if (response.headers.totalitems) {
      total = response.headers.totalitems;
    }

    return new GetUsersResponse(response.data, total);
  }

  @Action
  async getFacilityDetails(facilityCode): Promise<FacilityDetailsResource> {
    const response = await rawApi.get<FacilityDetailsResource>(`/facilities/${facilityCode}`);

    return response.data;
  }

  @Action
  async SaveFacilityDetails(request: SaveFacilityDetails): Promise<FacilityDetailsResource> {
    let response: AxiosResponse<FacilityDetailsResource>;

    let formattedRequest = {...request}
    formattedRequest.phone = formattedRequest.phone.replace(/\D/g, '')
    formattedRequest.fax = formattedRequest.fax.replace(/\D/g, '')
    formattedRequest.tollFreePhone = formattedRequest.tollFreePhone.replace(/\D/g, '')
    formattedRequest.customerServicePhone = formattedRequest.customerServicePhone.replace(/\D/g, '')


    if (!request.isNew)
      response = await rawApi.put<FacilityDetailsResource>(`/facilities/${formattedRequest.facilityCode}`, formattedRequest);
    else
      response = await rawApi.post<FacilityDetailsResource>(`/facilities`, formattedRequest);

    return response.data;
  }

  @Action
  async saveAgentDeliveryType(payload: any) {
    await rawApi.post(`/deliveryTypes`, payload);
  }

  @Action
  async updateAgentDeliveryType(payload: any) {
    await rawApi.put(`/deliveryTypes/agent-delivery-types`, payload);
  }

  @Action
  async loadAgentDeliveryTypes(agentId: number) {
    const result = await rawApi.get("/deliveryTypes/agent-delivery-types/" + agentId);
    this.setAgentDeliveryTypes(result.data);
  }

  @Mutation
  setAgentDeliveryTypes(payload: any) {
    this.agentDeliveryTypes = payload;
  }

  @Mutation
  setCurrentModal(modal: string) {
    this.modalToDisplay = modal;
  }

  @Mutation
  setModalData(data: any) {
    this.modalData = data;
  }

  @Mutation
  setErrorDetails(data: any) {
    this.errorDetails = data;
  }

  @Mutation
  addModalSeletedItem(data: any) {
    if (this.modalData.selectedItems) this.modalData.selectedItems.push(data);
  }

  @Mutation
  setOrderStatuses(data: OrderStatusResource[]) {
    this.orderStatuses = data;
  }

  @Mutation
  setTruckStatuses(data: TruckStatusResource[]) {
    this.truckStatuses = data;
  }

  @Mutation
  setChargeTypes(data: ChargeTypeResource[]) {
    this.chargeTypes = data;
  }

  @Mutation
  setDeliveryFors(deliveryFors: DeliveryForListResource[]) {
    this.deliveryFors = deliveryFors;
  }

  @Mutation
  setFacilities(facilities: FacilityListResource[]) {
    this.facilities = facilities;
    this.cdsFacilities = facilities.filter(x => x.isCDSOwnedFacility).map(function(x) {return x.facilityCode;});
  }

  @Mutation
  setAgentFacilities(agentFacilities: FacilityListResource[]) {
    this.agentFacilities = agentFacilities;
  }

  @Mutation
  setDeliveryTypes(deliveryTypes: DeliveryTypeListResource[]) {
    this.deliveryTypes = deliveryTypes;
  }

  @Mutation
  setReferenceTypes(referenceTypes: ReferenceTypeListResource[]) {
    this.referenceTypes = referenceTypes;
  }

  @Mutation
  setCommodities(commodities: Commodity[]) {
    this.commodities = commodities;
  }

  @Mutation
  setStates(states: State[]) {
    this.states = states;
  }

  @Mutation
  setZip3Overrides(zip3Overrides: Zip3Override[]) {
    this.zip3Overrides = zip3Overrides;
  }

  @Mutation
  setReasonCodes(codes: ReasonCodeResource[]) {
    this.reasonCodes = codes;
  }

  @Mutation
  setActionResults(results: any[]) {
    this.actionResults = results;
  }

  @Mutation
  setSaving(isSaving: boolean) {
    this.saving = isSaving;
  }

  @Mutation
  setCommodityFixForOptions(results: CommodityFixForOption[]) {
    this.commodityFixForOptions = results;
  }

  @Mutation
  setAllAgentDeliveryTypes(results: any[]) {
    this.allAgentDeliveryTypes = results;
  }
}
